/**
 * Date Format Parser
 *
 * Supports Australian (DD/MM/YYYY), US/Amex (MM/DD/YYYY), ISO (YYYY-MM-DD),
 * and DD MMM YYYY. Handles both date-only and datetime formats.
 */

/**
 * Parse date from common formats: DD/MM/YYYY (AU), MM/DD/YYYY (US/Amex), YYYY-MM-DD, DD MMM YYYY
 * @param dateString - Date string
 * @returns Parsed Date object or null if invalid
 */
export function parseAustralianDate(dateString: string): Date | null {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const trimmed = dateString.trim();

  // Try DD/MM/YYYY format (Australian - tried first)
  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?$/);
  if (slashMatch) {
    const first = parseInt(slashMatch[1], 10);
    const second = parseInt(slashMatch[2], 10);
    const year = parseInt(slashMatch[3], 10);
    const hours = slashMatch[4] ? parseInt(slashMatch[4], 10) : 0;
    const minutes = slashMatch[5] ? parseInt(slashMatch[5], 10) : 0;
    const seconds = slashMatch[6] ? parseInt(slashMatch[6], 10) : 0;
    // Australian: day/month/year
    const dateDDMM = new Date(year, second - 1, first, hours, minutes, seconds);
    if (dateDDMM.getDate() === first && dateDDMM.getMonth() === second - 1 && dateDDMM.getFullYear() === year) {
      return dateDDMM;
    }
    // US/Amex: month/day/year (try if DD/MM invalid, e.g. month 13)
    const dateMMDD = new Date(year, first - 1, second, hours, minutes, seconds);
    if (dateMMDD.getDate() === second && dateMMDD.getMonth() === first - 1 && dateMMDD.getFullYear() === year) {
      return dateMMDD;
    }
  }

  // Try DD-MM-YYYY format
  const dashMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?$/);
  if (dashMatch) {
    const day = parseInt(dashMatch[1], 10);
    const month = parseInt(dashMatch[2], 10) - 1;
    const year = parseInt(dashMatch[3], 10);
    const hours = dashMatch[4] ? parseInt(dashMatch[4], 10) : 0;
    const minutes = dashMatch[5] ? parseInt(dashMatch[5], 10) : 0;
    const seconds = dashMatch[6] ? parseInt(dashMatch[6], 10) : 0;
    const date = new Date(year, month, day, hours, minutes, seconds);
    if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
      return date;
    }
  }

  // Try YYYY-MM-DD (ISO)
  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?$/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10) - 1;
    const day = parseInt(isoMatch[3], 10);
    const hours = isoMatch[4] ? parseInt(isoMatch[4], 10) : 0;
    const minutes = isoMatch[5] ? parseInt(isoMatch[5], 10) : 0;
    const seconds = isoMatch[6] ? parseInt(isoMatch[6], 10) : 0;
    const date = new Date(year, month, day, hours, minutes, seconds);
    if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
      return date;
    }
  }

  // Try DD MMM YYYY format (e.g., "30 Nov 2025")
  const monthAbbrMatch = trimmed.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (monthAbbrMatch) {
    const day = parseInt(monthAbbrMatch[1], 10);
    const monthAbbr = monthAbbrMatch[2].toLowerCase();
    const year = parseInt(monthAbbrMatch[3], 10);
    const monthMap: Record<string, number> = {
      'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
      'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
    };
    const month = monthMap[monthAbbr];
    if (month !== undefined) {
      const date = new Date(year, month, day);
      if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
        return date;
      }
    }
  }

  return null;
}

/**
 * Format date to ISO string (YYYY-MM-DD)
 * @param date - Date object
 * @returns ISO date string
 */
export function formatDateToISO(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse Australian date and convert to ISO string
 * @param dateString - Date string in Australian format
 * @returns ISO date string or null if invalid
 */
export function parseAustralianDateToISO(dateString: string): string | null {
  const date = parseAustralianDate(dateString);
  if (!date) {
    return null;
  }
  return formatDateToISO(date);
}

